<template>
  <div
    v-if="(productItems?.length > 0)"
    class="cart-upsell__inner"
  >
    <button
      v-for="(product) in productItems"
      :key="product.variantId"
      :title="product.actionLabel"
      type="button"
      class="cart-upsell__item"
      :class="[
        product.included || activeUpsellItems.includes(`${product.variantId}`) ? 'is-active' : '',
        product.included ? 'is-included' : ''
      ]"
      :data-variant-id="product.variantId"
      :disabled="!product.available || isLoading"
      @click.prevent="handleUpsellClick(product)"
    >
      <div class="cart-upsell__item--active">
        <i class="icon icon--yellow-checkmark cart-upsell__item-icon"></i>
        <div class="cart-upsell__item-image">
          <ResponsiveImage
            :image="product.image"
            :image-alt="product.title.replace(/\[/g, '<sup>').replace(/\]/g, '</sup>')"
            modifier="square"
          />
        </div>
      </div>
      <div class="cart-upsell__item-details">
        <p v-html="product.title.replace(/\[/g, '<sup>').replace(/\]/g, '</sup>')"></p>
        <p>{{ product.priceLabel || `+ ${product.moneyPrice}` }}</p>
      </div>
      <span class="screenreader">{{ product.actionLabel }}</span>
    </button>
  </div>
</template>
<script>
import {
  computed,
  ref,
  toRefs,
  onBeforeMount,
  watch,
} from 'vue';
import { isChildLineOf } from '@framebridge/toolbox/upsellEligibility';
import useCart from '../composables/cart-actions';
import useCartGiftBox from '../composables/cart-gift-box';
import ResponsiveImage from '../common/responsive-image/ResponsiveImage';

export default {

  name: 'CartUpsell',
  components: {
    ResponsiveImage,
  },
  props: {
    parentProductId: {
      type: String,
      default: '',
    },
    parentFrameSpecId: {
      type: String,
      default: '',
    },
    parentProductProperties: {
      type: Object,
      default: () => ({}),
    },
    parentProductQty: {
      type: Number,
      default: 1,
    },
    products: {
      type: Array,
      default: () => [],
    },
    bagItems: {
      type: Array,
      default: () => [],
    },
    frameSpec: {
      type: Object,
      default: null,
    },
    currency: {
      type: String,
      default: 'USD',
    },
    isWishlist: {
      type: Boolean,
      default: false,
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    updateLoadingState: {
      type: Function,
      default: () => {},
    },
  },

  setup(props) {
    const {
      addUpsellItem,
      removeUpsellItem,
      fetchCart,
    } = useCart();
    const { disableGiftBox, enableGiftBox } = useCartGiftBox();
    const {
      parentProductQty,
      parentProductProperties,
      parentProductId,
      parentFrameSpecId,
      products,
      bagItems,
      frameSpec,
      currency,
      isWishlist,
    } = toRefs(props);

    onBeforeMount(() => {
      const {
        _upsell_parent: upsellParent,
        _upsell_item_variant_id_list: upsellVariantList,
      } = parentProductProperties.value;

      if (upsellParent && upsellParent === 'true' && upsellVariantList) {
        upsellVariantList.split(',')
          .filter((item) => !activeUpsellItems.value.includes(item))
          .forEach((item) => {
            activeUpsellItems.value.push(item);
          });
      }
    });

    // eslint-disable-next-line no-underscore-dangle
    const activeUpsellItems = ref([]);
    const siblingUpsellVariantIds = computed(() => (bagItems.value || [])
      .filter((item) => isChildLineOf(item, parentProductProperties.value))
      .map((item) => `${item.variant_id}`));

    watch(siblingUpsellVariantIds, (variantIds) => {
      variantIds
        .filter((variantId) => !activeUpsellItems.value.includes(variantId))
        .forEach((variantId) => activeUpsellItems.value.push(variantId));
    }, { immediate: true });
    const moneyFormatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency?.value || 'USD',
    });

    const productItems = computed(() => products.value.map((product) => {
      const {
        available,
        id,
        title,
        price,
        featured_image: image,
        variants,
        kind,
        joinerySku,
        included,
        priceLabel,
      } = product || {};

      if (!title || price === undefined || price === null || !variants) {
        return null;
      }
      const money = price / 100;
      const moneyPrice = moneyFormatter.format(money);
      const variantId = variants[0].id;
      const displayTitle = title.replace(/\[/g, '<sup>').replace(/\]/g, '</sup>');

      return {
        actionLabel: included ? `${displayTitle} is included with your order` : `Add ${displayTitle} to your order`,
        available,
        id,
        image,
        included,
        joinerySku,
        kind,
        moneyPrice,
        price,
        priceLabel,
        title,
        variantId,
      };
    }).filter(Boolean));

    /**
     * Handle Upsell Click
     * @param {Object} product
     * @returns {void}
     */
    const handleUpsellClick = (product) => {
      if (product.included) {
        return;
      }

      const disableLoading = () => {
        props.updateLoadingState(false);
      };
      const toggleUpSellActive = () => {
        if (!activeUpsellItems.value.includes(`${product.variantId}`)) {
          activeUpsellItems.value.push(`${product.variantId}`);
        }
        disableLoading();
      };
      const setInactive = () => {
        activeUpsellItems.value = activeUpsellItems.value.filter((i) => i !== `${product.variantId}`);
        disableLoading();
      };

      props.updateLoadingState(true);

      const isActive = activeUpsellItems.value.includes(`${product.variantId}`);

      if (product.kind === 'gift-box') {
        const toggle = isActive
          ? disableGiftBox({
            frameSpec: frameSpec.value,
            parentLineId: parentProductId.value,
            shopifyVariantId: product.variantId,
          })
          : enableGiftBox({
            frameSpec: frameSpec.value,
            parentLineId: parentProductId.value,
            joinerySku: product.joinerySku,
          });

        toggle
          .then(isActive ? setInactive : toggleUpSellActive)
          .catch((err) => {
            console.error('cart gift box toggle error: ', err);
            disableLoading();
            fetchCart();
          });

        return;
      }

      if (isActive) {
        removeUpsellItem(
          product.variantId,
          parentProductId.value,
          parentProductProperties.value,
          0,
          isWishlist.value,
          setInactive,
          product.price,
        );
      } else {
        addUpsellItem(
          product.variantId,
          product.price,
          parentProductId.value,
          parentFrameSpecId.value,
          parentProductProperties.value,
          parentProductQty.value,
          isWishlist.value,
          toggleUpSellActive,
        );
      }
    };

    return {
      productItems,
      activeUpsellItems,
      handleUpsellClick,
    };
  },
};
</script>
